import Card from "../../../../../components/layouts/card/card";
import PolicyStore from "../../../stores/policy-store";
import { SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import { DISPLAY_FEE_OPTION, FEE_TYPE } from "../../../policy-types";
import {
	CHARGE_FEES,
	DISPLAY_FEE_OPTION_MAP,
	FEE_OPTION_MAP,
	FEE_STORE_LEVEL,
	getDisplayFeeOptionMap,
} from "../../../constants";
import { FormFieldLine } from "../../../../../components/form-field-line/form-field-line";
import CancellationFee from "./cancellation-fee/cancellation-fee";
import RestockingFee from "./restocking-fee/restocking-fee";
import ReturnFee from "./return-fee/return-fee";
import ShippingLabelFee from "./shipping-label-fee/shipping-label-fee";
import "./policy-additional-payments-card.scss";
import { observer } from "mobx-react";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import { IValidationGroup } from "../../../../../hooks/validation-group/validation-group-hook";
import Hr from "../../../../../components/hr/hr";
import { ExpandCallapseStore } from "../../../../../hooks/expand-collapse-hook";
import { Note, NOTE_TYPE } from "../../../../../components/note/note";
import { resolveFeesCapability, IResolutionFeesCapability } from "../../../utils/resolve-fees-capability";
import { useEffect, useMemo } from "react";

interface IProps {
    policy: PolicyStore;
    validationGroup: IValidationGroup;
    expandCollapseStore: ExpandCallapseStore;
}

function getFilteredChargeFeesOptions(capability: IResolutionFeesCapability) {
    return Object.values(FEE_OPTION_MAP).filter(
        option => capability.supportedChargeFeesOptions.includes(option.value)
    );
}

function getFilteredNoFeesDisplayOptions(capability: IResolutionFeesCapability, chargeFees: CHARGE_FEES) {
    const baseOptions = getDisplayFeeOptionMap(chargeFees);
    return baseOptions.filter(
        option => capability.supportedNoFeesDisplayOptions.includes(option.value as DISPLAY_FEE_OPTION)
    );
}

const PolicyAdditionalPaymentsCard: React.FC<IProps> = observer(({ policy, validationGroup, expandCollapseStore }) => {
    const feesCapability = useMemo(
        () => resolveFeesCapability(policy.selectedResolutionId),
        [policy.selectedResolutionId]
    );

    const chargeFeesOptions = getFilteredChargeFeesOptions(feesCapability);
    const isChargeFeesForced = chargeFeesOptions.length === 1;
    const hasAnyFeeSupported = (
		feesCapability.isRestockingFeeSupported ||
		feesCapability.isReturnFeeSupported ||
		feesCapability.supportedShippingLabelFeeTypes.length > 0 ||
		chargeFeesOptions.length > 0
    );

    useEffect(() => {
        if (isChargeFeesForced && chargeFeesOptions[0].value !== policy.chargeFees) {
            policy.setChargeFees(chargeFeesOptions[0].value);
        } else if (!feesCapability.supportedChargeFeesOptions.includes(policy.chargeFees)) {
            policy.setChargeFees(chargeFeesOptions[0]?.value ?? CHARGE_FEES.DO_NOT_CHARGE_FEE);
        }
    }, [policy.selectedResolutionId, policy.chargeFees]);

    useEffect(() => {
        const noFeesDisplayOptions = getFilteredNoFeesDisplayOptions(feesCapability, policy.chargeFees);
        if (noFeesDisplayOptions.length === 1) {
            policy.setDisplayFeeOption(noFeesDisplayOptions[0].value as DISPLAY_FEE_OPTION);
        }
    }, [policy.selectedResolutionId, policy.chargeFees]);

    if (!hasAnyFeeSupported) {
        return null;
    }

    const noFeesDisplayOptions = getFilteredNoFeesDisplayOptions(feesCapability, policy.chargeFees);
    const isNoFeesDisplayForced = noFeesDisplayOptions.length === 1;

    return (
        <Card
            toolbar={{
                title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Additional Payments</FontBody1>
            }}
            isExpanded={expandCollapseStore.getIsExpanded('additional-payments-card')}
            expandCallback={(v) => expandCollapseStore.setExpanded('additional-payments-card', v)}
        >
            <FormFieldLine
                label='Fees'
                note='Define how fees will be applied when customers select this policy rule.'
            >
                <SelectDropdown
                    value={FEE_OPTION_MAP[policy.chargeFees] ?? null}
                    options={chargeFeesOptions}
                    onChange={(value) => policy.setChargeFees(value?.value as CHARGE_FEES)}
                    validationGroup={validationGroup}
                    required={ true }
                    isSearchable={false}
                    disabled={policy.isFeesDisabled || isChargeFeesForced}
                />
            </FormFieldLine>
            <Hr />

            {policy.chargeFees === CHARGE_FEES.DO_NOT_CHARGE_FEE && <FormFieldLine
                label='No Fees Display'
                note='Specify how to display to the customer that there are no fees.'
            >
                <SelectDropdown
                    value={DISPLAY_FEE_OPTION_MAP[policy.displayFeeOption] ?? null}
                    options={noFeesDisplayOptions}
                    onChange={(value) => policy.setDisplayFeeOption(value?.value as DISPLAY_FEE_OPTION)}
                    validationGroup={validationGroup}
                    required={ true }
                    isSearchable={false}
                    disabled={isNoFeesDisplayForced}
                />
            </FormFieldLine>}
            {policy.chargeFees === CHARGE_FEES.WAIVE_FEES &&
                <>
                    <FormFieldLine
                        label='Display'
                        note='Choose what is displayed to the customer for all policy rules.'
                    >
                        <SelectDropdown
                            value={DISPLAY_FEE_OPTION_MAP[policy.displayFeeOption] ?? null}
                            options={Object.values(DISPLAY_FEE_OPTION_MAP)}
                            onChange={(value) => policy.setDisplayFeeOption(value?.value as DISPLAY_FEE_OPTION)}
                            validationGroup={validationGroup}
                            required={ true }
                            isSearchable={false}
                        />
                    </FormFieldLine>
                    <Note
                        type={NOTE_TYPE.INFORMATION}
                        body={<>
                            When this policy rule is selected, it will override all other policy rule fee settings for the RMA.
                        </>}
                    />
                </>
            }

            <CancellationFee fee={policy.getFee(FEE_STORE_LEVEL.CANCEL)} validationGroup={validationGroup} />
            {feesCapability.isRestockingFeeSupported && (
                <RestockingFee fee={policy.getFee(FEE_STORE_LEVEL.RESTOCK)} validationGroup={validationGroup} />
            )}
            {feesCapability.isReturnFeeSupported && (
                <ReturnFee fee={policy.getFee(FEE_STORE_LEVEL.RETURN)} validationGroup={validationGroup} />
            )}
            {feesCapability.supportedShippingLabelFeeTypes.length > 0 && (
                <ShippingLabelFee
                    fee={policy.getFee(FEE_STORE_LEVEL.SHIPPING_LABEL)}
                    validationGroup={validationGroup}
                    hasRCRM={policy.hasRCRM}
                    supportedShippingLabelFeeTypes={feesCapability.supportedShippingLabelFeeTypes}
                />
            )}
        </Card>
    );
});

export default PolicyAdditionalPaymentsCard;
